import React, { useState, useRef } from 'react';
import { formatCEP, onlyNumbers } from '../../utils/formatUtils';

/**
 * Componente de autocompletar de endereço usando o ViaCEP
 * Preenche automaticamente os campos de endereço a partir do CEP
 */
const AddressAutocomplete = ({ 
  onAddressChange,
  initialData = {}
}) => {
  const [address, setAddress] = useState({
    cep: initialData.cep || '',
    street: initialData.street || '',
    number: initialData.number || '',
    complement: initialData.complement || '',
    neighborhood: initialData.neighborhood || '',
    city: initialData.city || '',
    state: initialData.state || ''
  });
  
  // Referência para o campo de número
  const numberInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Validar formato do CEP
  const isValidCep = (cep) => {
    // Remove caracteres não numéricos para verificação
    const numericCep = onlyNumbers(cep);
    return numericCep.length === 8;
  };
  
  // Função para buscar o endereço pelo CEP usando a API do ViaCEP
  const fetchAddressByCep = async (cep) => {
    // Garantir que o CEP está no formato numérico para a API
    const numericCep = onlyNumbers(cep);
    
    if (!numericCep || numericCep.length !== 8) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${numericCep}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        setError('CEP não encontrado. Verifique o CEP informado.');
        return;
      }
      
      const newAddress = {
        ...address,
        cep: numericCep, // Garante que o CEP completo é armazenado
        street: data.logradouro,
        neighborhood: data.bairro,
        city: data.localidade,
        state: data.uf
      };
      
      setAddress(newAddress);
      
      // Notificar o componente pai sobre a mudança
      if (onAddressChange) {
        onAddressChange(newAddress);
      }
      
      // Focar no campo de número após o CEP ser preenchido
      focusNumberField();
    } catch (err) {
      setError('Ocorreu um erro ao buscar o endereço. Tente novamente.');
      console.error('Erro ao buscar CEP:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Função para focar com segurança no campo de número - compatível com todos os navegadores
  const focusNumberField = () => {
    // Aguardar para garantir que o DOM foi atualizado e o campo está pronto
    setTimeout(() => {
      try {
        if (numberInputRef.current) {
          numberInputRef.current.focus();
          
          // Em alguns navegadores móveis, precisamos rolar até o campo
          // para garantir que ele está visível após o foco
          const yOffset = -150; // Ajuste para garantir visibilidade
          const y = numberInputRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
          
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }
      } catch (e) {
        console.warn('Não foi possível focar no campo de número:', e);
      }
    }, 300); // Tempo suficiente para garantir que a renderização foi concluída
  };
  
  // Manipular mudança nos campos do formulário
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Se o campo for CEP, formatar e buscar endereço
    if (name === 'cep') {
      // Remover caracteres não numéricos para processamento interno
      const numericCep = onlyNumbers(value);
      
      // Guardar o CEP numérico no estado
      const newAddress = {
        ...address,
        [name]: numericCep
      };
      
      setAddress(newAddress);
      
      // Limpar erro se estiver digitando
      if (error && numericCep.length < 8) {
        setError(null);
      }
      
      // Se o CEP estiver completo, buscar endereço
      // Importante: verificar o comprimento APÓS a remoção dos caracteres não numéricos
      if (numericCep.length === 8) {
        // Pequeno timeout para garantir que o estado foi atualizado
        setTimeout(() => {
          fetchAddressByCep(numericCep);
        }, 100);
      }
    } else {
      const newAddress = {
        ...address,
        [name]: value
      };
      
      setAddress(newAddress);
      
      // Atualizar componente pai
      if (onAddressChange) {
        onAddressChange(newAddress);
      }
    }
  };
  
  // Manipular colagem no campo de CEP
  const handleCepPaste = (e) => {
    // Pegar o conteúdo colado
    const pastedText = e.clipboardData.getData('Text');
    
    // Processar o conteúdo colado para extrair apenas os números
    const numericCep = onlyNumbers(pastedText);
    
    // Se o CEP colado estiver completo (8 dígitos), evitamos problemas de formatação
    // definindo o valor diretamente e buscando o endereço
    if (numericCep.length === 8) {
      e.preventDefault(); // Impedir o comportamento padrão de colagem
      
      // Atualizar o estado com o CEP numérico
      const newAddress = {
        ...address,
        cep: numericCep
      };
      
      setAddress(newAddress);
      
      // Buscar o endereço com o CEP colado
      setTimeout(() => {
        fetchAddressByCep(numericCep);
      }, 100);
    }
  };
  
  // Manipular a perda de foco do campo CEP
  const handleCepBlur = () => {
    const cep = address.cep;
    
    // Se o CEP estiver incompleto, mostrar erro
    if (cep && !isValidCep(cep)) {
      setError('CEP inválido. Digite um CEP com 8 dígitos.');
    } 
    // Se o CEP estiver completo, buscar endereço
    else if (cep && isValidCep(cep)) {
      fetchAddressByCep(cep);
    }
  };
  
  // Manipular tecla pressionada no campo de CEP
  const handleCepKeyDown = (e) => {
    // Se pressionar Tab ou Enter com o CEP completo
    if ((e.key === 'Tab' || e.key === 'Enter') && isValidCep(address.cep)) {
      // Verificar se o CEP já foi buscado (street preenchido)
      // Se não foi, buscar agora
      if (!address.street) {
        fetchAddressByCep(address.cep);
        
        // Se for Tab, evita o comportamento padrão para garantir
        // que nosso focusNumberField() funcionará corretamente
        if (e.key === 'Tab' && !e.shiftKey) {
          e.preventDefault();
        }
      }
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Campo de CEP */}
      <div>
        <label htmlFor="cep" className="block text-sm font-medium text-gray-700 mb-1">
          CEP
        </label>
        <div className="relative">
          <input
            type="text"
            id="cep"
            name="cep"
            maxLength="9"
            className={`w-full px-3 py-2 border ${error ? 'border-red-300' : 'border-gray-300'} rounded-md focus:ring-blue-500 focus:border-blue-500`}
            value={formatCEP(address.cep)}
            onChange={handleChange}
            onPaste={handleCepPaste}
            onBlur={handleCepBlur}
            onKeyDown={handleCepKeyDown}
            placeholder="00000-000"
            inputMode="numeric"
            autoComplete="postal-code"
          />
          {loading && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>
      
      {/* Campos de endereço */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
            Rua
          </label>
          <input
            type="text"
            id="street"
            name="street"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            value={address.street}
            onChange={handleChange}
            autoComplete="street-address"
          />
        </div>
        
        <div>
          <label htmlFor="number" className="block text-sm font-medium text-gray-700 mb-1">
            Número
          </label>
          <input
            type="text"
            id="number"
            name="number"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            value={address.number}
            onChange={handleChange}
            inputMode="numeric"
            ref={numberInputRef}
            autoComplete="address-line2"
          />
        </div>
        
        <div>
          <label htmlFor="complement" className="block text-sm font-medium text-gray-700 mb-1">
            Complemento
          </label>
          <input
            type="text"
            id="complement"
            name="complement"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            value={address.complement}
            onChange={handleChange}
            placeholder="Opcional"
            autoComplete="address-line3"
          />
        </div>
        
        <div>
          <label htmlFor="neighborhood" className="block text-sm font-medium text-gray-700 mb-1">
            Bairro
          </label>
          <input
            type="text"
            id="neighborhood"
            name="neighborhood"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            value={address.neighborhood}
            onChange={handleChange}
            autoComplete="address-level3"
          />
        </div>
        
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
            Cidade
          </label>
          <input
            type="text"
            id="city"
            name="city"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            value={address.city}
            onChange={handleChange}
            autoComplete="address-level2"
          />
        </div>
        
        <div>
          <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
            Estado
          </label>
          <input
            type="text"
            id="state"
            name="state"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            value={address.state}
            onChange={handleChange}
            maxLength="2"
            autoComplete="address-level1"
          />
        </div>
      </div>
    </div>
  );
};

export default AddressAutocomplete; 